import {
  getPhoneDigits,
  normalizeRussianLocalPhoneNumber,
} from '@app/components/Phone/utils';
import type { NDictionaries } from '@app/types';

/**
 * Найденный в тексте сообщения номер телефона.
 * `start`/`end` — границы в исходной строке, `digits` — только цифры в
 * международном виде, `countryId` — страна из словаря, чьей длине он подошёл.
 */
export interface LeadMessagePhone {
  start: number;
  end: number;
  raw: string;
  digits: string;
  countryId: number;
}

// Кандидат: необязательный «+», затем цифры вперемешку с разделителями,
// которыми люди разбивают номер. Скобки закрываем явно, чтобы не съесть
// хвост предложения.
const PHONE_CANDIDATE_REGEXP = /\+?\d[\d\s()\-–—.]{6,}\d/g;

// Дата вида 12.05.2026 или 12.05.26 — цифр достаточно, чтобы попасть в
// кандидаты, но номером она не является.
const DATE_LIKE_REGEXP = /^\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}$/;

const trimCandidate = (
  value: string,
  start: number
): { raw: string; start: number } => {
  let raw = value;
  let nextStart = start;

  // Хвостовые разделители — часть предложения, а не номера.
  while (raw.length > 0 && /[\s()\-–—.]/.test(raw[raw.length - 1])) {
    raw = raw.slice(0, -1);
  }

  while (raw.length > 0 && /[\s()\-–—.]/.test(raw[0])) {
    raw = raw.slice(1);
    nextStart += 1;
  }

  return { raw, start: nextStart };
};

const matchCountry = (
  digits: string,
  countries: NDictionaries.ModelCountry[]
): NDictionaries.ModelCountry | undefined =>
  countries
    .filter(({ TelCode }) => {
      const code = getPhoneDigits(TelCode ?? '');
      return code.length > 0 && digits.startsWith(code);
    })
    // Более длинный код точнее: у 7 и 77 разные страны.
    .sort(
      (a, b) =>
        getPhoneDigits(b.TelCode ?? '').length -
        getPhoneDigits(a.TelCode ?? '').length
    )
    .find(({ MinDigits, MaxDigits }) => {
      const min = MinDigits || 0;
      const max = MaxDigits || 0;
      if (!min || !max) return false;

      return digits.length >= min && digits.length <= max;
    });

/**
 * Ищет в тексте номера телефонов. Длину и код страны проверяем по словарю
 * стран — libphonenumber в проекте нет, а свои константы длин разъехались бы
 * со справочником, по которому валидируются поля телефона.
 */
export const findLeadMessagePhones = (
  text: string,
  countries: NDictionaries.ModelCountry[]
): LeadMessagePhone[] => {
  if (!text || !countries.length) return [];

  const found: LeadMessagePhone[] = [];
  const matches = text.matchAll(PHONE_CANDIDATE_REGEXP);

  for (const match of matches) {
    if (match.index === undefined) continue;

    const { raw, start } = trimCandidate(match[0], match.index);
    if (!raw || DATE_LIKE_REGEXP.test(raw)) continue;

    const digits = normalizeRussianLocalPhoneNumber(getPhoneDigits(raw));
    const country = matchCountry(digits, countries);

    if (!country) continue;

    found.push({
      start,
      end: start + raw.length,
      raw,
      digits,
      countryId: country.Id,
    });
  }

  return found;
};
